import { trackQuickFiltersEvents } from '@modules/charts/components/ChartTooltipWrapper/helpers/trackQuickFiltersEvents';
import { EMPTY_ARRAY } from '@modules/common/constants';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { Category } from '@modules/common/types';
import {
  formatNumToStandardNotation,
  getAggregatePeriodLabel,
  getFieldNameFromEqlField,
  getMetadataQBFieldFromEqlField,
} from '@modules/common/utils';
import { getFormattedAnomalousTimeRange } from '@modules/quantify/components/QuantifyAnomaliesTableView/helpers';
import { useGetQuantifyAggregatePeriod } from '@modules/quantify/hooks';
import { getLabelColumn } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { getQuickFilterParamsForField } from '@modules/queryBuilder/helpers';
import { useAddQuickFilter } from '@modules/queryBuilder/hooks';
import { FilterAltRounded, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import pluralize from 'pluralize';
import { UUID } from 'v2/core/entities/uuid';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface GetAnomaliesGridColumnsParams {
  onRecordsCountClick: (params: {
    id: UUID;
    label: string;
    category?: Category;
    isInvisible: boolean;
    isUpdateInProgress: boolean;
  }) => void;
  anomalousTimestamp: number;
}

export const useGetAnomaliesGridColumns = ({
  onRecordsCountClick,
  anomalousTimestamp,
}: GetAnomaliesGridColumnsParams): GridColDef[] => {
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const formulaID = useQuantifyStore(
    ({ query }) => query.formulaQuery?.eqlFunction,
  );
  const aggregatePeriod = useGetQuantifyAggregatePeriod();
  const quantifyDataPoints =
    useQuantifyStore(({ reportData }) => reportData.quantify.tableRows) ??
    EMPTY_ARRAY;

  const addQuickFilterV2 = useAddQuickFilter();

  const recordsCountSymbol =
    formulaID === EQLFunctionName.NormaliseByPercent ? '%' : '#';

  const aggregatePeriodLabel = getAggregatePeriodLabel(aggregatePeriod);
  const formattedAnomalousTimeRange = getFormattedAnomalousTimeRange({
    anomalousTimestamp,
    aggregatePeriod,
  });

  const fieldName = groupBy ? getFieldNameFromEqlField(groupBy) : undefined;
  const metadataQbField = groupBy
    ? getMetadataQBFieldFromEqlField(groupBy)
    : undefined;

  const hasCoOccurringDataPointInData = quantifyDataPoints.some(
    (tableRow) => !tableRow.isRelevant,
  );

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const labelColumn = getLabelColumn({
    groupBy,
    isCompactTableVariant: true,
    hasCoOccurringDataPointInData,
    shouldRemoveFileUploadPrefix,
    isUnifiedFieldEnabled,
  });

  return [
    labelColumn,
    {
      field: 'expectedFeedbackCount',
      minWidth: 120,
      type: 'number',
      sortable: false,
      renderCell: (params) => {
        return <Typography variant="body1">{params.value}</Typography>;
      },
      renderHeader: () => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ lineHeight: '18px' }}
          >
            <Stack direction="row" spacing={0.5}>
              <Typography variant="subtitle2" lineHeight="inherit">
                {recordsCountSymbol}
              </Typography>
              <Box>
                <Typography variant="subtitle2" lineHeight="inherit">
                  Expected
                </Typography>
                <Typography variant="subtitle2" lineHeight="inherit">
                  Records
                </Typography>
              </Box>
            </Stack>
            <Tooltip
              arrow
              title={`Expected feedback volume for the ${aggregatePeriodLabel} of 
              ${formattedAnomalousTimeRange}, based on the average of data from previous ${pluralize(
                aggregatePeriodLabel,
              )}.`}
              placement="top"
            >
              <InfoOutlined
                sx={{
                  color: 'text.secondary',
                }}
                fontSize="small"
              />
            </Tooltip>
          </Stack>
        );
      },
      valueGetter: (params) => {
        const expectedFeedbackCount =
          params.row.variants?.[anomalousTimestamp.toString()]?.anomalyValue
            ?.mean ?? 0;

        return formulaID === EQLFunctionName.NormaliseByPercent
          ? expectedFeedbackCount
          : formatNumToStandardNotation(Math.round(expectedFeedbackCount));
      },
    },
    {
      field: 'feedbackCount',
      headerName: `${recordsCountSymbol} Records`,
      minWidth: 50,
      type: 'number',
      sortable: false,
      renderCell: (params) => {
        return (
          <Tooltip title="View Feedback" placement="top" disableInteractive>
            <Link
              component="button"
              variant="body1"
              onClick={(event) => {
                event.stopPropagation();
                onRecordsCountClick({
                  id: params.row.id,
                  label: params.row.label,
                  category: params.row.category,
                  isInvisible: params.row.isInvisible,
                  isUpdateInProgress: params.row.isUpdateInProgress,
                });
              }}
              color="primary"
              underline="hover"
            >
              {formatNumToStandardNotation(params.value)}
            </Link>
          </Tooltip>
        );
      },
      valueGetter: (params) => {
        return params.row.variants?.[anomalousTimestamp.toString()]?.value ?? 0;
      },
    },
    {
      field: 'addToFilterAction',
      renderHeader: () => null,
      width: 30,
      type: 'actions',
      renderCell: (params) => {
        return (
          <Tooltip title="Add to filter" placement="top" disableInteractive>
            <IconButton
              className="js-add-to-filter-action"
              size="small"
              sx={{
                opacity: 0,
              }}
              onClick={() => {
                if (fieldName) {
                  const quickFilterParams = getQuickFilterParamsForField({
                    fieldName,
                    metadataQbField: metadataQbField,
                    value: params.row.id,
                    category: params.row.category,
                  });
                  addQuickFilterV2(quickFilterParams);
                  trackQuickFiltersEvents({
                    eventName: 'Quick Filter Added',
                  });
                }
              }}
            >
              <FilterAltRounded />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];
};
